import { roundNumber } from '../../utils/number';

type FuturePEMultipleData = {
  revenueYear0: number;
  revenueGrowthRate: number;
  profitMargin: number;
  futurePE: number;
  presentSharesOutstanding: number;
  discountRate: number;
  timeFrame: number;
};

export type FuturePEMultipleDataWithSharesOutstandingGrowthRate = {
  sharesOutstandingGrowthRate: number;
} & FuturePEMultipleData;

export const calculateRevenueGrowthRate = ({
  fairValue,
  discountRate,
  profitMargin,
  futurePE,
  revenueYear0,
  timeFrame,
  sharesOutstanding,
  sharesOutstandingGrowthRate,
}) => {
  const discountRatePercentage = discountRate / 100;
  const profitMarginPercentage = profitMargin / 100;

  const futureSharesOutstanding =
    sharesOutstanding * (1 + sharesOutstandingGrowthRate) ** timeFrame;
  const numerator =
    fairValue *
    (1 + discountRatePercentage) ** timeFrame *
    futureSharesOutstanding;
  const denominator = profitMarginPercentage * futurePE;

  // Prevent division by 0
  if (!revenueYear0 || !denominator) {
    return 0;
  }

  const radicand = numerator / denominator / revenueYear0;
  const estimatedRevenueGrowth = (radicand ** (1 / timeFrame) - 1) * 100;

  return roundNumber(estimatedRevenueGrowth, 2);
};

export const calculateFutureSharesOutstanding = ({
  presentSharesOutstanding,
  sharesOutstandingGrowthRate,
  timeFrame,
}) => {
  return Math.round(
    presentSharesOutstanding * (1 + sharesOutstandingGrowthRate) ** timeFrame
  );
};

export const calculateFairValue = (
  data: FuturePEMultipleDataWithSharesOutstandingGrowthRate
): number => {
  const {
    revenueYear0,
    revenueGrowthRate,
    profitMargin,
    futurePE,
    presentSharesOutstanding,
    discountRate,
    timeFrame,
    sharesOutstandingGrowthRate,
  } = data;
  const revenueGrowRatePercentage = revenueGrowthRate / 100;
  const profitMarginPercentage = profitMargin / 100;
  const discountRatePercentage = discountRate / 100;

  const futureSharesOutstanding = calculateFutureSharesOutstanding({
    presentSharesOutstanding,
    sharesOutstandingGrowthRate,
    timeFrame,
  });

  // Company may have not reported, such as new companies, which is impossible to calculate fair value
  if (revenueYear0 === 0) {
    return 0;
  }

  const result =
    (revenueYear0 *
      (1 + revenueGrowRatePercentage) ** timeFrame *
      profitMarginPercentage *
      futurePE) /
    futureSharesOutstanding /
    (1 + discountRatePercentage) ** timeFrame;

  return result > 0 ? roundNumber(result, 2) : 0;
};

export const calculateFutureRevenue = ({
  revenueYear0,
  revenueGrowthRatePercentage,
  timeFrame,
}: {
  revenueYear0: number;
  revenueGrowthRatePercentage: number;
  timeFrame: number;
}) => {
  return roundNumber(
    revenueYear0 * (1 + revenueGrowthRatePercentage / 100) ** timeFrame,
    2
  );
};

export const calculateFutureEarnings = ({
  futureRevenue,
  profitMarginPercentage,
}: {
  futureRevenue: number;
  profitMarginPercentage: number;
}) => {
  return roundNumber((profitMarginPercentage / 100) * futureRevenue, 2);
};

export const calculateFutureMarketCapitalisation = ({
  futureEarnings,
  futurePE,
}: {
  futureEarnings: number;
  futurePE: number;
}) => {
  return roundNumber(futureEarnings * futurePE, 2);
};

export const calculateSharePriceFutureYear = ({
  futureMarketCapitalisation,
  futureSharesOutstanding,
}: {
  futureMarketCapitalisation: number;
  futureSharesOutstanding: number;
}) => {
  return roundNumber(futureMarketCapitalisation / futureSharesOutstanding, 2);
};
